import React, { useState } from 'react';
import { TrendingUp, ShieldCheck } from 'lucide-react';
import TrackingRow from './TrackingRow';

const TrackingView = ({ formatCurrency, operations, currentPrices }) => {
  const [expandedCategory, setExpandedCategory] = useState(null);

  // Calculate monthly tracking metrics
  const buyOperations = operations.filter(op => op.type === 'Buy');
  const sellOperations = operations.filter(op => op.type === 'Sell');

  // Separate liquidity operations from investment operations
  const liquidityOperations = buyOperations.filter(op => op.assetType === 'Liquidity');
  const investmentOperations = buyOperations.filter(op => op.assetType !== 'Liquidity');

  // Total invested in assets (excluding liquidity)
  const totalInvested = investmentOperations.reduce((sum, op) => {
    return sum + (parseFloat(op.quantity || 0) * parseFloat(op.price || 0));
  }, 0);

  // Current portfolio value (investments only)
  const currentValue = investmentOperations.reduce((sum, op) => {
    const currentPrice = currentPrices[op.name] || parseFloat(op.price || 0);
    return sum + (parseFloat(op.quantity || 0) * currentPrice);
  }, 0);

  // Total sold
  const totalSold = sellOperations.reduce((sum, op) => {
    return sum + (parseFloat(op.quantity || 0) * parseFloat(op.price || 0));
  }, 0);

  // Cash liquidity (money available)
  const cashLiquidity = liquidityOperations.reduce((sum, op) => {
    return sum + (parseFloat(op.quantity || 0) * parseFloat(op.price || 0));
  }, 0);

  // Net income (total portfolio value minus initial investments)
  const netIncome = currentValue - totalSold + cashLiquidity;

  // Total liquidity (cash available)
  const liquidity = cashLiquidity;

  // Investment ratio (percentage of income invested in assets)
  const investmentRatio = netIncome > 0 ? (totalInvested / netIncome) * 100 : 0;

  // Calculate allocation breakdown by asset type (including liquidity)
  const allocationByType = {};
  buyOperations.forEach(op => {
    const currentPrice = currentPrices[op.name] || parseFloat(op.price || 0);
    const value = parseFloat(op.quantity || 0) * currentPrice;
    const type = op.assetType || 'Other';

    if (!allocationByType[type]) {
      allocationByType[type] = 0;
    }
    allocationByType[type] += value;
  });

  const totalAllocation = Object.values(allocationByType).reduce((sum, val) => sum + val, 0);

  const allocationRows = Object.entries(allocationByType).map(([type, amount]) => ({
    category: type,
    amount: formatCurrency(amount),
    progress: totalAllocation > 0 ? (amount / totalAllocation) * 100 : 0,
    operations: buyOperations.filter(op => (op.assetType || 'Other') === type)
  }));

  const toggleCategoryExpansion = (category) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4">
        <div>
          <p className="text-xs font-bold text-[#44474c] uppercase tracking-widest mb-1">Monthly Allocation</p>
          <h1 className="text-4xl font-extrabold text-[#041627] font-headline">{formatCurrency(totalInvested)}</h1>
        </div>
        <div className="bg-[#6bfe9c]/20 text-[#006d37] px-4 py-2 rounded-xl flex items-center gap-2">
          <TrendingUp size={16} /> <span className="font-bold">{investmentRatio.toFixed(1)}%</span> <span className="text-[10px] uppercase">Invest. Ratio</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Net Income</p>
          <p className="text-2xl font-bold">{formatCurrency(netIncome)}</p>
        </div>
        <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Total Invested</p>
          <p className="text-2xl font-bold">{formatCurrency(totalInvested)}</p>
        </div>
        <div className="bg-[#041627] text-white p-6 rounded-xl shadow-lg relative overflow-hidden">
          <p className="text-xs font-bold opacity-60 uppercase tracking-widest mb-2">Liquidity</p>
          <p className="text-2xl font-bold">{formatCurrency(liquidity)}</p>
          <ShieldCheck className="absolute -right-4 -bottom-4 opacity-10" size={100} />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-50 flex justify-between">
          <h3 className="font-bold">Allocation Breakdown (Current Month)</h3>
          <button className="text-xs font-bold text-[#041627] uppercase underline">Export Report</button>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-[10px] uppercase font-bold text-slate-500">
            <tr>
              <th className="px-6 py-4">Category</th>
              <th className="px-6 py-4">Allocation</th>
              <th className="px-6 py-4">Share</th>
              <th className="px-6 py-4">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {allocationRows.length > 0 ? (
              allocationRows.map((row, index) => (
                <React.Fragment key={index}>
                  <TrackingRow
                    category={row.category}
                    amount={row.amount}
                    progress={row.progress}
                    isExpanded={expandedCategory === row.category}
                    onToggle={() => toggleCategoryExpansion(row.category)}
                  />
                  {expandedCategory === row.category && (
                    <tr className="bg-slate-25">
                      <td colSpan="4" className="px-6 py-4">
                        <div className="bg-slate-50 rounded-lg p-4">
                          <h4 className="font-bold text-sm text-[#041627] mb-3">Operations in {row.category}</h4>
                          <div className="space-y-2">
                            {row.operations.map((op, opIndex) => {
                              const currentPrice = currentPrices[op.name] || parseFloat(op.price || 0);
                              const currentValue = parseFloat(op.quantity || 0) * currentPrice;
                              const originalValue = parseFloat(op.quantity || 0) * parseFloat(op.price || 0);
                              const gainLoss = currentValue - originalValue;
                              const gainLossPercent = originalValue > 0 ? (gainLoss / originalValue) * 100 : 0;

                              return (
                                <div key={opIndex} className="flex justify-between items-center py-2 px-3 bg-white rounded border">
                                  <div className="flex-1">
                                    <div className="font-medium text-sm">{op.name}</div>
                                    <div className="text-xs text-slate-500">
                                      {op.quantity} units @ {formatCurrency(parseFloat(op.price || 0))}
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className="font-medium text-sm">{formatCurrency(currentValue)}</div>
                                    <div className={`text-xs ${gainLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      {gainLoss >= 0 ? '+' : ''}{formatCurrency(gainLoss)} ({gainLossPercent.toFixed(1)}%)
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="px-6 py-8 text-center text-slate-500">
                  No investments found. Add some operations to see allocation breakdown.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
};

export default TrackingView;